<template>
  <section class="customer-cases" ref="casesSection">
    <div class="container">
      <div class="section-header" :class="{ 'fade-in': isCasesVisible }">
        <h2 class="section-title">
          <span class="title-text">客户案例</span>
        </h2>
        <div class="section-subtitle">专业服务能力，值得信赖的合作伙伴</div>
      </div>
      
      <div class="cases-container">
        <div class="cases-wrapper">
          <div v-for="(item, index) in cases" 
            :key="index"
            class="case-box"
            :style="{
              transform: `translateY(${(index - currentIndex) * 100}%) ${isHovered ? 'scale(1.02)' : ''}`,
              opacity: currentIndex === index ? 1 : 0,
              visibility: Math.abs(currentIndex - index) <= 1 ? 'visible' : 'hidden'
            }"
            @mouseenter="isHovered = true"
            @mouseleave="isHovered = false"
          >
            <div class="case-icon">
              <img :src="baseUrl + '/media' + item.cover" :alt="item.title" />
            </div>
            <div class="case-content">
              <h3 class="title">{{ item.title }}</h3>
              <p class="description">{{ stripHtml(item.content) }}</p>
              <div class="client-tags" v-if="item.keywords">
                <div class="tags">
                  <span v-for="(tag, idx) in item.keywords.split('、')" 
                        :key="idx" 
                        class="tag">
                    {{ tag }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 右侧指示点 -->
        <div class="cases-indicators">
          <div 
            v-for="(_, index) in cases" 
            :key="index"
            class="indicator"
            :class="{ active: currentIndex === index }"
            @click="setCase(index)"
          >
            <span class="indicator-dot"></span>
            <span class="indicator-text">0{{ index + 1 }}</span>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import axios from 'axios'
import { getBaseApiUrl } from '@/utils/api-config'

const baseUrl = getBaseApiUrl()
const casesSection = ref(null)
const isCasesVisible = ref(false)
const isHovered = ref(false)
const cases = ref([])
const currentIndex = ref(0)
const autoPlayInterval = ref(null)

// 获取案例数据
const getCasesData = async () => {
  try {
    const response = await axios.get(`${baseUrl}/api/article/article/`, {
      params: {
        page: 1,
        limit: 5,
        status: 'published',
        column: 8
      }
    })
    if (response.data.code === 2000) {
      cases.value = response.data.data.data
    }
  } catch (error) {
    console.error('获取案例数据失败:', error)
  }
}

const nextCase = () => {
  if (currentIndex.value < cases.value.length - 1) {
    currentIndex.value++
  } else {
    currentIndex.value = 0
  }
}

const setCase = (index) => {
  currentIndex.value = index
}

const startAutoPlay = () => {
  autoPlayInterval.value = setInterval(() => {
    nextCase()
  }, 5000)
}

const stopAutoPlay = () => {
  if (autoPlayInterval.value) {
    clearInterval(autoPlayInterval.value)
  }
}

const handleScroll = () => {
  if (casesSection.value) {
    const rect = casesSection.value.getBoundingClientRect()
    const triggerPoint = window.innerHeight * 0.7
    isCasesVisible.value = rect.top <= triggerPoint
  }
}

// 去除 HTML 标签只保留文本
const stripHtml = (html) => {
  if (!html) return ''
  const div = document.createElement('div')
  div.innerHTML = html
  return div.textContent || div.innerText || ''
}

onMounted(() => {
  getCasesData() // 获取案例数据
  window.addEventListener('scroll', handleScroll)
  handleScroll()
  startAutoPlay()
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
  stopAutoPlay()
})
</script>

<style lang="scss" scoped>
.customer-cases {
  padding: 80px 0;
  background-color: #f6fdff;
  overflow: hidden;

  .container {
    max-width: 1200px;
    margin: 0 auto;
  }

  .section-header {
    text-align: center;
    margin-bottom: 60px;
    opacity: 0;
    transform: translateY(20px);
    transition: all 0.8s ease;

    &.fade-in {
      opacity: 1;
      transform: translateY(0);
    }

    .section-title {
      position: relative;
      display: inline-block;
      margin-bottom: 16px;

      .title-text {
        position: relative;
        z-index: 1;
        font-size: 36px;
        font-weight: 600;
        color: var(--el-text-color-primary);
        
        &::after {
          content: '';
          position: absolute;
          left: 0;
          bottom: -2px;
          width: 100%;
          height: 16px;
          background: linear-gradient(90deg, rgba(64, 158, 255, 0.3) 0%, rgba(64, 158, 255, 0) 100%);
          z-index: -1;
        }
      }
    }

    .section-subtitle {
      font-size: 16px;
      color: var(--el-text-color-secondary);
      margin-top: 8px;
    }
  }

  .cases-container {
    position: relative;
    overflow: hidden;
    margin: 0 auto;
    max-width: 1200px;
    height: 400px;
    padding-right: 60px;
  }

  .cases-wrapper {
    position: relative;
    height: 100%;
  }

  .case-box {
    position: absolute;
    top: 0;
    left: 0;
    width: calc(100% - 60px);
    height: 100%;
    background: #fff;
    border-radius: 15px;
    box-shadow: 0 4px 57px rgba(64, 158, 255, 0.08);
    padding: 40px;
    transition: all 0.5s ease;
    display: flex;
    gap: 40px;

    .case-icon {
      flex-shrink: 0;
      width: 320px;
      height: 320px;
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
      border-radius: 10px;
      background-color: #f5f7fa;
      
      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        transition: transform 0.3s ease;
      }
    }

    &:hover {
      .case-icon img {
        transform: scale(1.05);
      }
    }

    .case-content {
      flex: 1;
      display: flex;
      flex-direction: column;
      justify-content: flex-start;
      padding: 0;

      .title {
        font-size: 28px;
        margin-bottom: 20px;
        color: #333;
        padding-top: 10px;
      }

      .description {
        font-size: 16px;
        line-height: 1.8;
        color: #666;
        margin-bottom: 30px;
        display: -webkit-box;
        -webkit-line-clamp: 4;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }

      .client-tags {
        .tags {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;

          .tag {
            background: rgba(64, 158, 255, 0.1);
            color: var(--el-color-primary);
            padding: 6px 12px;
            border-radius: 4px;
            font-size: 14px;
            transition: all 0.3s;

            &:hover {
              background: rgba(64, 158, 255, 0.2);
            }
          }
        }
      }
    }
  }

  .cases-indicators {
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    flex-direction: column;
    gap: 20px;
    z-index: 10;
    padding: 15px;

    .indicator {
      display: flex;
      align-items: center;
      gap: 8px;
      cursor: pointer;
      transition: all 0.3s ease;
      opacity: 0.5;

      &:hover {
        opacity: 1;
      }

      &.active {
        opacity: 1;

        .indicator-dot {
          transform: scale(1.2);
          background-color: var(--el-color-primary);
        }

        .indicator-text {
          color: var(--el-color-primary);
          font-weight: 600;
        }
      }

      .indicator-dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background-color: #666;
        transition: all 0.3s ease;
      }

      .indicator-text {
        font-size: 14px;
        color: #666;
        transition: all 0.3s ease;
        font-family: 'DIN Alternate', sans-serif;
      }
    }
  }

  @media (max-width: 1200px) {
    .case-box {
      .case-icon {
        width: 280px;
        height: 280px;
      }
    }
  }

  @media (max-width: 768px) {
    padding: 40px 20px;

    .cases-container {
      padding-right: 40px;
    }

    .case-box {
      flex-direction: column;
      padding: 20px;
      gap: 20px;

      .case-icon {
        width: 100%;
        height: 200px;
      }

      .case-content {
        padding: 0;
        justify-content: flex-start;
      }
    }

    .cases-indicators {
      padding: 10px;
      gap: 15px;

      .indicator {
        .indicator-dot {
          width: 6px;
          height: 6px;
        }

        .indicator-text {
          font-size: 12px;
        }
      }
    }
  }
}
</style> 